import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { isPlan } from 'src/components/utils'
import { IPlan, IUnit } from 'src/types/filterPlans.interfaces'
import { IShell } from 'src/types/filterShells.interfaces'

export type GroupedPlan = [string, IPlan[] | IUnit[] | IShell[]]

const MAX_SELECTED_PLANS = 4
const INITIAL_SELECTED_PLANS_ACTIVE_ELEVATIONS: [
  string,
  IPlan | IUnit | IShell,
][] = []
const INITIAL_SELECTED_PLANS: GroupedPlan[] = []

export interface SelectedPlansState {
  allPlansActiveElevationIndices: Record<string, string>
  isAddingMorePlans?: boolean
  isPlanCompareOpen: boolean
  maxSelectionReached: boolean
  selectedPlans: GroupedPlan[]
  selectedPlansActiveElevation: [string, IPlan | IUnit | IShell][]
}

const initialState: SelectedPlansState = {
  allPlansActiveElevationIndices: {},
  isAddingMorePlans: false,
  isPlanCompareOpen: false,
  maxSelectionReached: false,
  selectedPlans: INITIAL_SELECTED_PLANS,
  selectedPlansActiveElevation: INITIAL_SELECTED_PLANS_ACTIVE_ELEVATIONS,
}

// Helper function to get plan identifier (plan key)
const getPlanIdentifier = (planGroup: GroupedPlan): string => planGroup[0]

// Helper function to check if plan group is already selected
const isPlanGroupSelected = (
  selectedPlans: GroupedPlan[],
  planGroup: GroupedPlan
): boolean =>
  selectedPlans.some(
    (selectedPlanGroup) =>
      getPlanIdentifier(selectedPlanGroup) === getPlanIdentifier(planGroup)
  )

export const selectedPlansSlice = createSlice({
  initialState,
  name: 'selectedPlans',
  reducers: {
    clearAllPlansActiveElevations: (state) => {
      state.allPlansActiveElevationIndices = {}
    },
    clearSelectedPlans: (state) => {
      state.selectedPlans = INITIAL_SELECTED_PLANS
      state.selectedPlansActiveElevation =
        INITIAL_SELECTED_PLANS_ACTIVE_ELEVATIONS
      state.maxSelectionReached = false
      state.isAddingMorePlans = false
    },

    setAllPlansActiveElevationIndex: (
      state,
      action: PayloadAction<{ basePlanId: string; elevationIndex: string }>
    ) => {
      const { basePlanId, elevationIndex } = action.payload
      state.allPlansActiveElevationIndices[basePlanId] = elevationIndex
    },

    setIsAddingMorePlans: (state, action: PayloadAction<boolean>) => {
      state.isAddingMorePlans = action.payload
    },
    setIsPlanCompareOpen: (state, action: PayloadAction<boolean>) => {
      state.isPlanCompareOpen = action.payload
    },
    setPlanActiveElevation: (
      state,
      action: PayloadAction<{ planId: string; elevationIndex: number }>
    ) => {
      const { elevationIndex, planId } = action.payload
      const groupedPlan = state.selectedPlans.find(
        (planGroup) => getPlanIdentifier(planGroup) === planId
      )
      if (!groupedPlan) {
        return
      }
      const elevations = groupedPlan[1]
      if (elevationIndex < 0 || elevationIndex >= elevations.length) {
        return
      }
      const newElevation = elevations[elevationIndex]

      const existingIndex = state.selectedPlansActiveElevation.findIndex(
        ([id]) => id === planId
      )
      if (existingIndex !== -1) {
        state.selectedPlansActiveElevation[existingIndex] = [
          planId,
          newElevation,
        ]
      } else {
        state.selectedPlansActiveElevation.push([planId, newElevation])
      }
    },

    toggleSelectedPlan: (state, action: PayloadAction<GroupedPlan>) => {
      const planGroup = action.payload

      if (isPlanGroupSelected(state.selectedPlans, planGroup)) {
        // Remove the plan group
        const planIdentifier = getPlanIdentifier(planGroup)
        state.selectedPlans = state.selectedPlans.filter(
          (selectedPlanGroup) =>
            getPlanIdentifier(selectedPlanGroup) !== planIdentifier
        )
        state.maxSelectionReached = false
        state.selectedPlansActiveElevation =
          state.selectedPlansActiveElevation.filter(
            (plan) =>
              (isPlan(plan[1]) ? plan[1].planId : plan[1].id) !==
              (isPlan(planGroup[1][0])
                ? planGroup[1][0].planId
                : planGroup[1][0].id)
          )
      } else if (state.selectedPlans.length < MAX_SELECTED_PLANS) {
        // Add the plan group
        state.selectedPlans.push(planGroup)
        state.maxSelectionReached =
          state.selectedPlans.length >= MAX_SELECTED_PLANS
        // Creating the selectedPlansActiveElevation array with the first plan of each group by default
        state.selectedPlansActiveElevation.push([planGroup[0], planGroup[1][0]])
      }
    },
  },
})

export const {
  clearAllPlansActiveElevations,
  clearSelectedPlans,
  setAllPlansActiveElevationIndex,
  setIsAddingMorePlans,
  setIsPlanCompareOpen,
  setPlanActiveElevation,
  toggleSelectedPlan,
} = selectedPlansSlice.actions
